import { Button, Popup, ScrollView, SelectBox } from "devextreme-react";
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { getTutorialSteps, TUTORIAL_MODULES } from "../config/tutorialSteps";

interface TourStep {
  title: string;
  content: string;
  stepKey?: string;
  area?: string;
  image?: string;
}

interface TutorialContextType {
  startStepTour: (steps: TourStep[], moduleKey?: string) => void;
  currentModule: string | null;
  setCurrentModule: (module: string | null) => void;
}

const TutorialContext = createContext<TutorialContextType | null>(null);

export function TutorialProvider({ children }: PropsWithChildren) {
  const { t } = useTranslation();

  const [stepTourVisible, setStepTourVisible] = useState(false);
  const [tourSteps, setTourSteps] = useState<TourStep[]>([]);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [currentModule, setCurrentModule] = useState<string | null>(null);
  const [selectedModule, setSelectedModule] = useState<string | null>(null);

  const isMobile =
    typeof window !== "undefined" ? window.innerWidth < 768 : false;

  const startStepTour = useCallback((steps: TourStep[], moduleKey?: string) => {
    setTourSteps(steps);
    setCurrentStepIndex(0);
    setStepTourVisible(true);
    setSelectedModule(moduleKey || null);
  }, []);

  const handleNextStep = () => {
    if (currentStepIndex < tourSteps.length - 1) {
      setCurrentStepIndex(currentStepIndex + 1);
    } else {
      setStepTourVisible(false);
      setCurrentStepIndex(0);
    }
  };

  const handlePrevStep = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(currentStepIndex - 1);
    }
  };

  const handleSkipTour = () => {
    setStepTourVisible(false);
    setCurrentStepIndex(0);
  };

  const renderStepContent = () => {
    const currentStep = tourSteps[currentStepIndex];
    if (!currentStep?.stepKey) return null;

    const stepData = t(currentStep.stepKey, { returnObjects: true }) as any;

    if (stepData?.content && typeof stepData.content === "string") {
      return <p>{stepData.content}</p>;
    }

    // Złożone kroki: intro + items/steps + note
    return (
      <div className="space-y-3">
        {stepData?.intro && <p>{stepData.intro}</p>}

        {/* Lista punktowana: pogrubienie pierwszej części przed – */}
        {stepData?.items && Array.isArray(stepData.items) && (
          <ul className="list-disc my-2 space-y-1 pl-5">
            {stepData.items.map((item: string, idx: number) => {
              const parts = item.split(" – ");
              return (
                <li key={idx}>
                  {parts.length > 1 ? (
                    <>
                      <strong className="font-semibold">{parts[0]}</strong>
                      {" – "}
                      {parts.slice(1).join(" – ")}
                    </>
                  ) : (
                    item
                  )}
                </li>
              );
            })}
          </ul>
        )}

        {/* Lista numerowana (kroki do wykonania): pogrubienie pierwszej części */}
        {stepData?.steps && Array.isArray(stepData.steps) && (
          <ol className="list-decimal my-2 space-y-1 pl-5">
            {stepData.steps.map((step: string, idx: number) => {
              const parts = step.split(" – ");
              return (
                <li key={idx}>
                  {parts.length > 1 ? (
                    <>
                      <strong className="font-semibold">{parts[0]}</strong>
                      {" – "}
                      {parts.slice(1).join(" – ")}
                    </>
                  ) : (
                    step
                  )}
                </li>
              );
            })}
          </ol>
        )}

        {/* Uwaga/notatka dodatkowa */}
        {stepData?.note && <p>{stepData.note}</p>}
      </div>
    );
  };

  const handleModuleChange = (moduleKey: string) => {
    setSelectedModule(moduleKey);
    const newSteps = getTutorialSteps(moduleKey, t, true);
    setTourSteps(newSteps);
    setCurrentStepIndex(0);
  };

  const currentStep = tourSteps[currentStepIndex];

  const moduleOptions = Object.keys(TUTORIAL_MODULES).map((key) => ({
    key,
    name: t(TUTORIAL_MODULES[key].moduleName),
  }));

  return (
    <TutorialContext.Provider
      value={{
        startStepTour,
        currentModule,
        setCurrentModule,
      }}
    >
      {children}

      {/* Popup samouczek*/}
      <Popup
        visible={stepTourVisible}
        fullScreen={isMobile}
        dragEnabled={!isMobile}
        hideOnOutsideClick={false}
        showCloseButton={true}
        showTitle
        title={t("tutorial.title")}
        width={isMobile ? "100vw" : 700}
        height={isMobile ? "100vh" : "auto"}
        maxHeight={isMobile ? "100vh" : "80vh"}
        position={
          isMobile
            ? "center"
            : { my: "top", at: "top", of: window, offset: "0 80" }
        }
        onHiding={handleSkipTour}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            height: isMobile ? "100vh" : "auto",
            overflow: "hidden",
          }}
        >
          <ScrollView
            width="100%"
            height="auto"
            showScrollbar="onScroll"
            useNative={true}
            scrollByContent={true}
            scrollByThumb={true}
            bounceEnabled={true}
            style={{
              flexGrow: 1,
              padding: isMobile ? "16px" : "20px",
              touchAction: "pan-y",
              WebkitOverflowScrolling: "touch",
            }}
          >
            <div className="flex flex-col gap-4">
              {/* Nagłówek z selectem modułu i nawigacją */}
              <div
                className={`flex ${
                  isMobile ? "flex-col gap-3" : "items-center gap-4"
                } mb-2`}
              >
                <div className="flex-grow">
                  <SelectBox
                    label={t("tutorial.area")}
                    labelMode="static"
                    value={selectedModule}
                    items={moduleOptions}
                    displayExpr="name"
                    valueExpr="key"
                    onValueChanged={(e) => handleModuleChange(e.value)}
                    width="100%"
                  />
                </div>

                {/* Licznik kroków ze strzałkami nawigacyjnymi */}
                <div
                  className={`flex items-center ${
                    isMobile ? "justify-between" : "gap-2"
                  } flex-shrink-0`}
                >
                  <Button
                    icon="chevrondoubleleft"
                    disabled={currentStepIndex === 0}
                    onClick={() => setCurrentStepIndex(0)}
                    stylingMode="text"
                    width={isMobile ? 40 : undefined}
                    height={isMobile ? 40 : undefined}
                    elementAttr={{
                      style: {
                        color: currentStepIndex === 0 ? "#ccc" : "#666",
                      },
                    }}
                  />
                  <Button
                    icon="chevronleft"
                    disabled={currentStepIndex === 0}
                    onClick={handlePrevStep}
                    stylingMode="text"
                    width={isMobile ? 40 : undefined}
                    height={isMobile ? 40 : undefined}
                    elementAttr={{
                      style: {
                        color: currentStepIndex === 0 ? "#ccc" : "#666",
                      },
                    }}
                  />
                  <div
                    className={`${
                      isMobile ? "text-base" : "text-sm"
                    } font-medium text-gray-700 dark:text-gray-300 min-w-[100px] text-center whitespace-nowrap`}
                  >
                    {t("tutorial.step", {
                      current: currentStepIndex + 1,
                      total: tourSteps.length,
                    })}
                  </div>
                  <Button
                    icon="chevronright"
                    disabled={currentStepIndex === tourSteps.length - 1}
                    onClick={handleNextStep}
                    stylingMode="text"
                    width={isMobile ? 40 : undefined}
                    height={isMobile ? 40 : undefined}
                    elementAttr={{
                      style: {
                        color:
                          currentStepIndex === tourSteps.length - 1
                            ? "#ccc"
                            : "#666",
                      },
                    }}
                  />
                  <Button
                    icon="chevrondoubleright"
                    disabled={currentStepIndex === tourSteps.length - 1}
                    onClick={() => setCurrentStepIndex(tourSteps.length - 1)}
                    stylingMode="text"
                    width={isMobile ? 40 : undefined}
                    height={isMobile ? 40 : undefined}
                    elementAttr={{
                      style: {
                        color:
                          currentStepIndex === tourSteps.length - 1
                            ? "#ccc"
                            : "#666",
                      },
                    }}
                  />
                </div>
              </div>

              {/* Treść kroku */}
              <div
                className={`${
                  isMobile ? "text-base" : "text-base"
                } leading-relaxed text-gray-800 dark:text-gray-200`}
                style={{
                  whiteSpace: "pre-wrap",
                }}
              >
                {renderStepContent()}
              </div>

              {/* Obrazek */}
              {currentStep?.image && (
                <div
                  key={currentStep.image}
                  className="flex justify-center my-4"
                >
                  <img
                    src={currentStep.image}
                    alt={currentStep.title || "Tutorial step"}
                    className="max-w-full h-auto rounded-lg shadow-md"
                    style={{ maxHeight: isMobile ? "300px" : "400px" }}
                    onError={(e) => {
                      const img = e.currentTarget as HTMLImageElement;
                      // console.warn("Obrazek nie znaleziony:", img.src);
                      const parent = img.parentElement;
                      if (parent) parent.style.display = "none";
                    }}
                  />
                </div>
              )}
            </div>
          </ScrollView>
        </div>
      </Popup>
    </TutorialContext.Provider>
  );
}

export const useTutorial = () => {
  const context = useContext(TutorialContext);
  if (!context) {
    throw new Error("useTutorial must be used within TutorialProvider");
  }
  return context;
};
